import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { projectsApi } from '../../api/projects';
import { sessionsApi } from '../../api/sessions';
import { storiesApi } from '../../api/stories';
import { plottrackPath } from '../../utils/plottrackPath';
import {
  IDEA_ROOM_TITLE,
  TURN_INTO_SCENES,
  WHICH_BOOK_CANCEL,
  WHICH_BOOK_FROM_CHAT,
  WHICH_BOOK_NEW,
  WHICH_BOOK_TITLE,
} from '../../utils/writerCopy';
import './WhichBookSheet.css';

const NEW_BOOK = '__new__';

function bookRows(rows, projectId) {
  return (rows || [])
    .filter((row) => row && row.id)
    .map((row) => ({
      id: row.id,
      name: row.name || '',
      projectId: row.project_id || projectId,
    }));
}

/** Books in this chat's project, or every book the writer has.
 *  Order is the order the list calls already return. */
export async function loadWriterBooks(projectId) {
  if (projectId) {
    const rows = await storiesApi.list(projectId);
    return bookRows(rows, projectId);
  }
  const projects = await projectsApi.list();
  const lists = await Promise.all((projects || []).map(async (project) => {
    const rows = await storiesApi.list(project.id);
    return bookRows(rows, project.id);
  }));
  return lists.flat();
}

/** Create a book with the calls the app already uses. */
export async function createWriterBook({ projectId, name }) {
  const title = String(name || '').trim();
  if (projectId) {
    const story = await storiesApi.create(projectId, { name: title });
    return story.id;
  }
  const project = await projectsApi.create({
    name: title,
    medium: 'novel',
    is_implicit: true,
    is_series: false,
  });
  if (project?.story_id) return project.story_id;
  const story = await storiesApi.create(project.id, { name: title });
  return story.id;
}

function focusableIn(node) {
  if (!node) return [];
  const all = [...node.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  )].filter((el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true');
  const radios = all.filter((el) => el.type === 'radio');
  const checked = radios.find((el) => el.checked) || radios[0];
  return all.filter((el) => el.type !== 'radio' || el === checked);
}

/**
 * Which book? — bottom sheet on a phone, centered dialog on a wider screen.
 * Picking a book attaches this chat, then Outline opens as it does today.
 */
export default function WhichBookSheet({
  open,
  chatName = '',
  projectId = null,
  sessionId = null,
  onClose,
  navigate,
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [books, setBooks] = useState([]);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setBooks([]);
    setChoice('');
    setBusy(false);
    loadWriterBooks(projectId)
      .then((rows) => {
        if (!cancelled) setBooks(rows);
      })
      .catch((err) => {
        console.error('Failed to load books:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return undefined;
    const node = dialogRef.current;
    if (!node) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    node.focus();

    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const list = focusableIn(node);
      if (list.length === 0) {
        event.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      const inside = node.contains(active) && active !== node;
      if (!inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    node.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = previous;
      node.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  if (!open) return null;

  const source = WHICH_BOOK_FROM_CHAT(chatName || IDEA_ROOM_TITLE);
  const canGo = Boolean(choice) && Boolean(sessionId) && !busy;
  const choiceIds = [...books.map((book) => book.id), NEW_BOOK];

  const onRadioKey = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
      event.preventDefault();
      setChoice(target.value);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const radios = choiceIds
      .map((id) => event.currentTarget.querySelector(`input[type="radio"][value="${id}"]`))
      .filter(Boolean);
    const index = radios.indexOf(target);
    if (index < 0) return;
    event.preventDefault();
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const next = radios[(index + (forward ? 1 : -1) + radios.length) % radios.length];
    next.focus();
    setChoice(next.value);
  };

  const confirm = async () => {
    if (!canGo) return;
    setBusy(true);
    try {
      const storyId = choice === NEW_BOOK
        ? await createWriterBook({
          projectId,
          name: chatName || IDEA_ROOM_TITLE,
        })
        : choice;
      await sessionsApi.attachBook(sessionId, storyId);
      onCloseRef.current();
      navigate(plottrackPath(storyId), { state: { startGenerate: true } });
    } catch (err) {
      console.error('Failed to attach this chat to a book:', err);
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="which-book__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCloseRef.current();
      }}
    >
      <div
        className="which-book"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 id={titleId} className="which-book__title">{WHICH_BOOK_TITLE}</h2>
        <p className="which-book__source">{source}</p>
        <div
          className="which-book__books"
          role="radiogroup"
          aria-labelledby={titleId}
          onKeyDown={onRadioKey}
        >
          {books.map((book) => (
            <label key={book.id} className="which-book__choice">
              <input
                type="radio"
                name="which-book"
                value={book.id}
                checked={choice === book.id}
                onChange={() => setChoice(book.id)}
              />
              <span>{book.name}</span>
            </label>
          ))}
          <label className="which-book__choice">
            <input
              type="radio"
              name="which-book"
              value={NEW_BOOK}
              checked={choice === NEW_BOOK}
              onChange={() => setChoice(NEW_BOOK)}
            />
            <span>{WHICH_BOOK_NEW}</span>
          </label>
        </div>
        <div className="which-book__actions">
          <button
            type="button"
            className="which-book__cancel"
            onClick={() => onCloseRef.current()}
            disabled={busy}
          >
            {WHICH_BOOK_CANCEL}
          </button>
          <button
            type="button"
            className="which-book__go"
            onClick={confirm}
            disabled={!canGo}
          >
            {TURN_INTO_SCENES}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
